import React, { useState, useRef } from "react";
import Sidebar from "../../Components/Sidebar";
import { useNavigate } from "react-router-dom";
import { TbAward } from "react-icons/tb";
import { PiTextColumnsLight } from "react-icons/pi";
import { FiUpload } from "react-icons/fi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { addScholarship } from "../../redux/actions/scholarshipActions";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import PageComponent from "../../Components/PageComponent";

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["bold", "italic", "underline"],
    [{ align: [] }],
    ["clean"],
  ],
};

const formats = ["font", "header", "list", "bold", "italic", "underline", "align"];

function AddScholarship() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: null,
    imagePreview: null,
    files: [],
  });
  const [isSaving, setIsSaving] = useState(false);

  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setFormData((prev) => ({ ...prev, image: file, imagePreview: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({ ...prev, files: [...prev.files, ...files] }));
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      image: null,
      imagePreview: null,
      files: [],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (!stripHtml(formData.description).trim()) {
      toast.error("Description is required.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    formDataToSend.append("description", formData.description);
    if (formData.image) formDataToSend.append("image", formData.image);
    formData.files.forEach((file) => formDataToSend.append("files", file));

    setIsSaving(true);
    try {
      await dispatch(addScholarship(formDataToSend));
      toast.success("Scholarship added successfully!");
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add scholarship. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <PageComponent>
        <div className="flex w-screen h-screen bg-[#F8F7FA] overflow-hidden">
          <div className="fixed top-0 left-0 w-64 h-full bg-white overflow-y-auto shadow-md">
            <Sidebar />
          </div>
          <div className="flex-1 min-h-screen p-6 ml-64 overflow-auto">
            <div className="flex items-center space-x-2 text-3xl text-gray-500">
              <span>Academics & International</span>
              <span>/</span>
              <span className="text-black">Scholarships</span>
            </div>

            <div className="flex items-center mt-4 space-x-4">
              <button
                className="flex items-center px-4 py-2 text-white bg-[#92288C] rounded-lg shadow hover:bg-[#6C0B67]"
                onClick={() => navigate("/academics&international/scholarships/addScholarship")}
              >
                <TbAward className="mr-2" />
                Add Scholarship
              </button>
              <button
                className="flex items-center px-4 py-2 text-[#A8AAAE] rounded-lg shadow hover:bg-gray-100"
                onClick={() => navigate("/academics&international/scholarships/allScholarships")}
              >
                <PiTextColumnsLight className="mr-2" />
                All Scholarships
              </button>
            </div>

            <div className="w-full p-6 mt-6 bg-white rounded-lg shadow-md">
              <h2 className="mb-4 text-xl font-semibold text-gray-700">Add New Scholarship</h2>
              <form onSubmit={handleSubmit}>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-600">Title</label>
                  <input
                    name="title"
                    type="text"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-1/2 px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                </div>

                <div className="mt-6">
                  <label className="block mb-1 text-gray-700">Description</label>
                  <ReactQuill
                    value={formData.description}
                    theme="snow"
                    modules={modules}
                    formats={formats}
                    onChange={handleQuillChange}
                    className="h-[150px] mb-1"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4 mt-16">
                  <div className="relative flex flex-col items-center justify-center w-full h-[225px] p-5 border border-gray-300 border-dashed rounded-md">
                    {formData.imagePreview ? (
                      <img
                        src={formData.imagePreview}
                        alt="Preview"
                        className="absolute inset-0 object-cover w-full h-full opacity-50"
                      />
                    ) : (
                      <>
                        <FiUpload className="w-10 h-10 p-2 mb-3 text-gray-400 bg-gray-100 border-none rounded-md" />
                        <p className="text-black">Drag and drop image here</p>
                        <p className="text-sm text-center text-gray-400">
                          Flyer size should 500 px 600 px (width x height)
                        </p>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="scholarship-image-upload"
                    />
                    <label
                      htmlFor="scholarship-image-upload"
                      className="px-4 py-2 mt-2 text-[#92288C] font-semibold bg-[#92288C] bg-opacity-10 rounded-md cursor-pointer z-10"
                    >
                      Browse Image
                    </label>
                  </div>

                  <div className="col-span-2">
                    <label className="block mb-1 text-sm font-medium text-gray-600">Supporting Documents</label>
                    <div className="flex flex-col items-center justify-center mt-2 border-2 border-gray-300 border-dashed rounded-lg h-36">
                      {formData.files.length === 0 ? (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current.click()}
                          className="px-4 py-2 text-[#92288C] rounded-md hover:underline"
                        >
                          Browse File
                        </button>
                      ) : null}
                    </div>

                    {formData.files.map((file, index) => (
                      <div key={index} className="flex items-center mt-4 space-x-4">
                        <span className="w-3/4 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          className="px-4 py-2 text-white bg-red-500 rounded-md hover:bg-red-600"
                          onClick={() => handleRemoveFile(index)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      multiple
                      onChange={handleFileChange}
                    />
                    {formData.files.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current.click()}
                        className="mt-4 text-sm text-[#92288C] hover:underline"
                      >
                        Add more
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="flex justify-end mt-6 space-x-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-[#A8AAAE] bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className={`px-4 py-2 text-white rounded-md ${
                      isSaving ? "bg-gray-400 cursor-not-allowed" : "bg-[#92288C] hover:bg-[#6C0B67]"
                    }`}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </PageComponent>
    </>
  );
}

export default AddScholarship;
